import { execFileSync } from 'node:child_process'

import * as p from '@clack/prompts'
import pc from 'picocolors'

export interface DatabasePromptResult {
  /** `vercel-cli` runs the automated provisioning flow; `manual` returns a pasted `url`. */
  provider: 'vercel-cli' | 'manual'
  url?: string
}

const VERCEL_NEON_URL = 'https://vercel.com/dashboard/integrations/checkout/neon'

/**
 * Prompt the user to choose how to connect their database. Selecting the Vercel
 * option defers to the automated CLI flow (run by the caller); the manual option
 * collects a connection string inline. When `allowVercel` is false (e.g.
 * `--no-vercel`), the Vercel option is hidden and we go straight to manual entry.
 */
export async function promptDatabase(options: {
  allowVercel: boolean
}): Promise<DatabasePromptResult> {
  if (!options.allowVercel) {
    const url = await promptConnectionString()
    return { provider: 'manual', url }
  }

  const choice = await p.select({
    message: 'How would you like to connect your PostgreSQL database?',
    options: [
      {
        value: 'vercel-neon' as const,
        label: 'Vercel (Neon)',
        hint: 'create & connect a free Postgres database via the Vercel CLI'
      },
      {
        value: 'manual' as const,
        label: 'Enter connection string manually'
      }
    ],
    initialValue: 'vercel-neon' as const
  })

  if (p.isCancel(choice)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  if (choice === 'vercel-neon') {
    return { provider: 'vercel-cli' }
  }

  const url = await promptConnectionString()
  return { provider: 'manual', url }
}

/** Browser fallback: open the Vercel Neon checkout when the CLI flow can't run. */
export function openBrowserVercelNeon(): void {
  openBrowser(VERCEL_NEON_URL)
  p.log.info(
    `Opening Vercel... Create a Neon Postgres database, then copy the ${pc.cyan('DATABASE_URL')} from the dashboard.`
  )
}

/** Text prompt for the connection string with quote-stripping validation. */
export async function promptConnectionString(): Promise<string> {
  const input = await p.text({
    message: 'Paste your PostgreSQL connection string',
    placeholder: 'postgres://user:pass@host/db',
    validate(val) {
      const normalized = val?.trim() ?? ''

      if (!normalized) {
        return 'A connection string is required to continue'
      }

      const stripped = normalized.replace(/^['"]|['"]$/g, '')
      if (!stripped.startsWith('postgres://') && !stripped.startsWith('postgresql://')) {
        return 'Must start with postgres:// or postgresql://'
      }
    }
  })

  if (p.isCancel(input)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  return (input as string).replace(/^['"]|['"]$/g, '').trim()
}

function openBrowser(url: string): void {
  try {
    const platform = process.platform
    if (platform === 'darwin') {
      execFileSync('open', [url], { stdio: 'ignore' })
    } else if (platform === 'win32') {
      execFileSync('cmd', ['/c', 'start', url], { stdio: 'ignore' })
    } else {
      execFileSync('xdg-open', [url], { stdio: 'ignore' })
    }
  } catch {
    // Browser opening is best effort; the prompt still accepts a pasted URL.
  }
}
